import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import PlanEditor from './PlanEditor';

const ArticlePlan = ({ 
  article, 
  selectedModel, 
  setSelectedModel, 
  models, 
  settings, 
  planLoading, 
  modelsLoading,
  onGeneratePlan,
  onSavePlan,
  onConfirmPlan 
}) => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(article.raw_plan || '');

  // Update currentPlan when article.raw_plan changes (e.g., after generation)
  useEffect(() => {
    const newPlan = article.raw_plan || '';
    setCurrentPlan(newPlan);
  }, [article.raw_plan]);

  if (article.paragraphs.length > 0) {
    return null;
  }

  const handlePlanChange = (newPlan) => {
    console.log('Plan changed:', newPlan); // Debug log
    setCurrentPlan(newPlan);
    // Save changes to the database without confirming
    onSavePlan(newPlan);
  };

  return (
    <>
      <Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          mb: 0.5
        }}>
          <Typography variant="h6" sx={{ mb: { xs: 0.5, sm: 0 } }}>
            Article Plan
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%'
            }}>
              <FormControl 
                size="small" 
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '32px'
                  }
                }}
              >
                <InputLabel>Model</InputLabel>
                <Select
                  value={selectedModel}
                  label="Model"
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={modelsLoading}
                  endAdornment={
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <Tooltip title="Set default model in project settings">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/settings?highlight=default_paragraph_planner_model`);
                          }}
                          sx={{ 
                            color: 'primary.main',
                            p: 0.5
                          }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '300px'
                      }
                    }
                  }}
                >
                  {models.map((model) => (
                    <MenuItem 
                      key={model.name} 
                      value={model.name}
                      sx={{ 
                        py: 1,
                        minHeight: '32px'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: '0.875rem'
                      }}>
                        <span>{model.name} ({model.provider})</span>
                        {model.name === settings?.default_paragraph_planner_model && (
                          <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                            (Default)
                          </Typography>
                        )}
                        {model.premium && (
                          <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              width: '100%'
            }}>
              <Button
                variant="contained"
                onClick={onGeneratePlan}
                disabled={planLoading}
                size="small"
                sx={{ 
                  flex: 1,
                  height: '32px',
                  whiteSpace: 'nowrap'
                }}
              >
                {planLoading ? 'Generating...' : 'Generate Plan'}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => onConfirmPlan(currentPlan)}
                size="small"
                sx={{ 
                  flex: 1,
                  height: '32px',
                  whiteSpace: 'nowrap'
                }}
              >
                Confirm Plan
              </Button>
            </Box>
          </Box>
        </Box>
        
        <PlanEditor
          rawPlan={currentPlan}
          onChange={handlePlanChange}
          onGeneratePlan={onGeneratePlan}
        />
      </Box>
    </>
  );
};

export default ArticlePlan; 